/**
 * @zendir/ui - ConnectionForm Component
 *
 * MQTT/WebSocket connection form with URL composition, recent connections,
 * URL parameter sharing, and persistence. Tailored for operator dashboards
 * that require broker configuration (host, port, protocol, credentials).
 *
 * Astro UX Compliance:
 * - Status indicators for connection state (connected/disconnected/error)
 * - Form validation with clear error states
 * - Keyboard shortcut for quick connect (Ctrl+Enter)
 *
 * @example
 * ```tsx
 * <ConnectionForm
 *   onConnect={(config) => client.connect(config)}
 *   onDisconnect={() => client.disconnect()}
 *   connected={isConnected}
 *   defaultProtocol="wss"
 *   showAdvanced
 * />
 * ```
 */

import React, {
  memo,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useId,
  useRef,
} from "react";
import { useTheme } from "../../theme";
import type { LabelPlacement } from "../inputs/Input";
import { useBreakpoint } from "../layout/useBreakpoint";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ConnectionConfig {
  /** Host */
  host: string;
  /** Port */
  port: number;
  /** Protocol */
  protocol: "ws" | "wss" | "mqtt" | "mqtts" | "tcp";
  /** Path (e.g. /mqtt) */
  path?: string;
  /** Username */
  username?: string;
  /** Password */
  password?: string;
  /** Client ID */
  clientId?: string;
}

export interface ConnectionFormProps {
  /** Connect handler */
  onConnect: (config: ConnectionConfig) => void;
  /** Disconnect handler */
  onDisconnect?: () => void;
  /** Current connection state */
  connected?: boolean;
  /** Connection in progress */
  connecting?: boolean;
  /** Default protocol */
  defaultProtocol?: ConnectionConfig["protocol"];
  /** Default host */
  defaultHost?: string;
  /** Default port */
  defaultPort?: number;
  /** Default path */
  defaultPath?: string;
  /** Show advanced fields (clientId, credentials) */
  showAdvanced?: boolean;
  /** Persist to localStorage (key name) */
  persistKey?: string;
  /** Parse URL params on mount */
  parseUrlParams?: boolean;
  /** Error message */
  error?: string | null;
  /** Compact layout */
  compact?: boolean;
  /** Custom style */
  style?: React.CSSProperties;
  /**
   * Label placement style for form fields.
   * - `'outlined'` — label sits on the input border (notched/Material-style). **Default.**
   * - `'above'`    — label sits above the input with a gap (classic stack).
   */
  labelPlacement?: LabelPlacement;
}

const PROTOCOLS: ConnectionConfig["protocol"][] = [
  "wss",
  "ws",
  "mqtts",
  "mqtt",
  "tcp",
];
const DEFAULT_PORTS: Record<string, number> = {
  ws: 80,
  wss: 443,
  mqtt: 1883,
  mqtts: 8883,
  tcp: 1883,
};

export const ConnectionForm = memo(function ConnectionForm({
  onConnect,
  onDisconnect,
  connected = false,
  connecting = false,
  defaultProtocol = "wss",
  defaultHost = "localhost",
  defaultPort,
  defaultPath = "/mqtt",
  showAdvanced = false,
  persistKey,
  parseUrlParams = false,
  error,
  compact = false,
  style,
  labelPlacement = "outlined",
}: ConnectionFormProps): React.ReactElement {
  const { tokens } = useTheme();
  const { isMobile } = useBreakpoint();
  const isOutlined = labelPlacement === "outlined";

  const initialConfig: ConnectionConfig = useMemo(() => {
    // Try localStorage
    if (persistKey) {
      try {
        const saved = localStorage.getItem(persistKey);
        if (saved) return JSON.parse(saved);
      } catch {
        /* ignore */
      }
    }

    // Try URL params
    if (parseUrlParams && typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.has("host")) {
        return {
          host: params.get("host") || defaultHost,
          port: parseInt(
            params.get("port") ||
              String(defaultPort || DEFAULT_PORTS[defaultProtocol]),
          ),
          protocol: (params.get("protocol") ||
            defaultProtocol) as ConnectionConfig["protocol"],
          path: params.get("path") || defaultPath,
          username: params.get("username") || "",
          password: params.get("password") || "",
          clientId: params.get("clientId") || "",
        };
      }
    }

    return {
      host: defaultHost,
      port: defaultPort || DEFAULT_PORTS[defaultProtocol],
      protocol: defaultProtocol,
      path: defaultPath,
      username: "",
      password: "",
      clientId: "",
    };
  }, []);

  const baseId = useId();
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const usernameInputRef = useRef<HTMLInputElement>(null);
  const [config, setConfig] = useState<ConnectionConfig>(initialConfig);
  const [showAdv, setShowAdv] = useState(showAdvanced);
  const [showPassword, setShowPassword] = useState(false);

  // Persist on change
  useEffect(() => {
    if (persistKey) {
      try {
        localStorage.setItem(persistKey, JSON.stringify(config));
      } catch {
        /* ignore */
      }
    }
  }, [config, persistKey]);

  const updateField = useCallback(
    <K extends keyof ConnectionConfig>(key: K, value: ConnectionConfig[K]) => {
      setConfig((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleProtocolChange = useCallback(
    (protocol: ConnectionConfig["protocol"]) => {
      setConfig((prev) => ({
        ...prev,
        protocol,
        port:
          prev.port === DEFAULT_PORTS[prev.protocol]
            ? DEFAULT_PORTS[protocol]
            : prev.port,
      }));
    },
    [],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!connected) onConnect(config);
      else onDisconnect?.();
    },
    [config, connected, onConnect, onDisconnect],
  );

  const getShareUrl = useCallback(() => {
    const base = window.location.origin + window.location.pathname;
    const params = new URLSearchParams({
      host: config.host,
      port: String(config.port),
      protocol: config.protocol,
      ...(config.path ? { path: config.path } : {}),
    });
    return `${base}?${params.toString()}`;
  }, [config]);

  const fullUrl = `${config.protocol}://${config.host}:${config.port}${config.path || ""}`;

  const inputStyle: React.CSSProperties = {
    width: "100%",
    minHeight: compact ? 32 : 40,
    padding: compact
      ? `6px ${tokens.spacing.sm}`
      : `${tokens.spacing.sm} ${tokens.spacing.md}`,
    fontSize: tokens.typography.fontSize.sm,
    fontFamily: tokens.typography.fontFamily.primary,
    color: tokens.colors.text.primary,
    backgroundColor: tokens.colors.background.base,
    border: `1px solid ${tokens.colors.border.default}`,
    borderRadius: tokens.borderRadius.md,
    outline: "none",
    transition: tokens.animation.fast,
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = isOutlined
    ? {
        position: "absolute",
        top: 0,
        left: "10px",
        transform: "translateY(-50%)",
        backgroundColor: tokens.colors.background.base,
        padding: "0 5px",
        fontSize: tokens.typography.fontSize.xxs,
        fontWeight: tokens.typography.fontWeight.medium,
        color: tokens.colors.text.tertiary,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        zIndex: 1,
        pointerEvents: "none",
        lineHeight: 1.2,
        whiteSpace: "nowrap",
      }
    : {
        display: "block",
        fontSize: tokens.typography.fontSize.xxs,
        fontWeight: tokens.typography.fontWeight.medium,
        color: tokens.colors.text.tertiary,
        marginBottom: "4px",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      };

  // Wrapper for outlined fields (position: relative so the label can sit on the border)
  const fieldWrapperStyle: React.CSSProperties = isOutlined
    ? { position: "relative" }
    : {};

  // Status dot
  const statusColor = connected
    ? tokens.colors.status.normal
    : error
      ? tokens.colors.status.critical
      : tokens.colors.status.off;

  return (
    <form
      onSubmit={handleSubmit}
      aria-label="Connection configuration"
      style={{
        fontFamily: tokens.typography.fontFamily.primary,
        ...style,
      }}
    >
      {/* Status bar */}
      <div
        aria-live="polite"
        style={{
          display: "flex",
          alignItems: "center",
          gap: tokens.spacing.sm,
          flexWrap: "wrap",
          marginBottom: tokens.spacing.md,
        }}
      >
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: statusColor,
            boxShadow: connected ? `0 0 6px ${statusColor}` : "none",
            transition: tokens.animation.fast,
          }}
        />
        <span
          style={{
            fontSize: tokens.typography.fontSize.xs,
            color: tokens.colors.text.secondary,
            fontWeight: tokens.typography.fontWeight.medium,
          }}
        >
          {connecting
            ? "Connecting..."
            : connected
              ? "Connected"
              : "Disconnected"}
        </span>
        <div style={{ flex: 1 }} />
        <span
          style={{
            fontSize: tokens.typography.fontSize.xxs,
            color: tokens.colors.text.tertiary,
            fontFamily: tokens.typography.fontFamily.mono,
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: isMobile ? "100%" : 280,
            whiteSpace: isMobile ? "normal" : "nowrap",
            wordBreak: isMobile ? "break-all" : "normal",
          }}
        >
          {fullUrl}
        </span>
      </div>

      {/* Main fields */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : "minmax(80px, 100px) 1fr minmax(60px, 80px)",
          gap: tokens.spacing.sm,
        }}
      >
        {/* Protocol */}
        <div style={fieldWrapperStyle}>
          <label htmlFor={`${baseId}-protocol`} style={labelStyle}>
            Protocol
          </label>
          <select
            id={`${baseId}-protocol`}
            value={config.protocol}
            onChange={(e) =>
              handleProtocolChange(
                e.target.value as ConnectionConfig["protocol"],
              )
            }
            disabled={connected || connecting}
            style={{
              ...inputStyle,
              cursor: "pointer",
              appearance: "auto",
            }}
          >
            {PROTOCOLS.map((p) => (
              <option key={p} value={p}>
                {p.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        {/* Host */}
        <div style={fieldWrapperStyle}>
          <label htmlFor={`${baseId}-host`} style={labelStyle}>
            Host
          </label>
          <input
            id={`${baseId}-host`}
            type="text"
            value={config.host}
            onChange={(e) => updateField("host", e.target.value)}
            disabled={connected || connecting}
            placeholder="broker.example.com"
            style={inputStyle}
          />
        </div>

        {/* Port */}
        <div style={fieldWrapperStyle}>
          <label htmlFor={`${baseId}-port`} style={labelStyle}>
            Port
          </label>
          <input
            id={`${baseId}-port`}
            type="number"
            value={config.port}
            min={1}
            max={65535}
            onChange={(e) => {
              const v = parseInt(e.target.value) || 0;
              updateField("port", Math.max(0, Math.min(65535, v)));
            }}
            disabled={connected || connecting}
            style={{
              ...inputStyle,
              fontFamily: tokens.typography.fontFamily.mono,
            }}
          />
        </div>
      </div>

      {/* Path */}
      <div style={{ marginTop: tokens.spacing.sm, ...fieldWrapperStyle }}>
        <label htmlFor={`${baseId}-path`} style={labelStyle}>
          Path
        </label>
        <input
          id={`${baseId}-path`}
          type="text"
          value={config.path || ""}
          onChange={(e) => updateField("path", e.target.value)}
          disabled={connected || connecting}
          placeholder="/mqtt"
          style={inputStyle}
        />
      </div>

      {/* Advanced toggle */}
      <button
        type="button"
        onClick={() => setShowAdv((p) => !p)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          fontSize: tokens.typography.fontSize.xxs,
          color: tokens.colors.text.tertiary,
          backgroundColor: "transparent",
          border: "none",
          cursor: "pointer",
          marginTop: tokens.spacing.sm,
          padding: 0,
          fontFamily: tokens.typography.fontFamily.primary,
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          style={{
            transition: tokens.animation.fast,
            transform: showAdv ? "rotate(90deg)" : "rotate(0)",
          }}
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
        Advanced
      </button>

      {/* Advanced fields */}
      {showAdv && (
        <div
          style={{
            marginTop: tokens.spacing.sm,
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: tokens.spacing.sm,
          }}
        >
          <div style={fieldWrapperStyle}>
            <label htmlFor={`${baseId}-username`} style={labelStyle}>
              Username
            </label>
            <input
              ref={usernameInputRef}
              id={`${baseId}-username`}
              type="text"
              value={config.username || ""}
              onChange={(e) => updateField("username", e.target.value)}
              onPaste={(e) => {
                const pasted = e.clipboardData.getData("text");
                const trimmed = pasted.trim();
                const input = usernameInputRef.current;
                if (input) {
                  const cur = config.username || "";
                  const start = input.selectionStart ?? cur.length;
                  const end = input.selectionEnd ?? cur.length;
                  const newValue =
                    cur.slice(0, start) + trimmed + cur.slice(end);
                  if (trimmed !== pasted || newValue !== cur) {
                    e.preventDefault();
                    updateField("username", newValue);
                    requestAnimationFrame(() => {
                      input.setSelectionRange(
                        start + trimmed.length,
                        start + trimmed.length,
                      );
                    });
                  }
                } else if (trimmed !== pasted) {
                  e.preventDefault();
                  updateField("username", trimmed);
                }
              }}
              onBlur={() => {
                const cur = config.username ?? "";
                const trimmed = cur.trim();
                if (trimmed !== cur) updateField("username", trimmed);
              }}
              disabled={connected || connecting}
              autoComplete="username"
              style={inputStyle}
            />
          </div>
          <div style={fieldWrapperStyle}>
            <label htmlFor={`${baseId}-password`} style={labelStyle}>
              Password
            </label>
            <div style={{ position: "relative" }}>
              <input
                ref={passwordInputRef}
                id={`${baseId}-password`}
                type={showPassword ? "text" : "password"}
                value={config.password || ""}
                onChange={(e) => updateField("password", e.target.value)}
                onPaste={(e) => {
                  const pasted = e.clipboardData.getData("text");
                  const trimmed = pasted.trim();
                  const input = passwordInputRef.current;
                  if (input) {
                    const cur = config.password || "";
                    const start = input.selectionStart ?? cur.length;
                    const end = input.selectionEnd ?? cur.length;
                    const newValue =
                      cur.slice(0, start) + trimmed + cur.slice(end);
                    if (trimmed !== pasted || newValue !== cur) {
                      e.preventDefault();
                      updateField("password", newValue);
                      requestAnimationFrame(() => {
                        const pos = start + trimmed.length;
                        input.setSelectionRange(pos, pos);
                      });
                    }
                  } else if (trimmed !== pasted) {
                    e.preventDefault();
                    updateField("password", trimmed);
                  }
                }}
                onBlur={() => {
                  const cur = config.password ?? "";
                  const trimmed = cur.trim();
                  if (trimmed !== cur) updateField("password", trimmed);
                }}
                disabled={connected || connecting}
                autoComplete="current-password"
                style={{ ...inputStyle, paddingRight: 36 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: tokens.colors.text.tertiary,
                  fontSize: tokens.typography.fontSize.xxs,
                  minHeight: 32,
                  padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
                }}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>
          <div style={{ gridColumn: "1 / -1", ...fieldWrapperStyle }}>
            <label htmlFor={`${baseId}-clientId`} style={labelStyle}>
              Client ID
            </label>
            <input
              id={`${baseId}-clientId`}
              type="text"
              value={config.clientId || ""}
              onChange={(e) => updateField("clientId", e.target.value)}
              disabled={connected || connecting}
              placeholder="auto-generated if empty"
              style={inputStyle}
            />
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          style={{
            marginTop: tokens.spacing.sm,
            padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
            fontSize: tokens.typography.fontSize.xs,
            color: tokens.colors.status.critical,
            backgroundColor: `${tokens.colors.status.critical}10`,
            border: `1px solid ${tokens.colors.status.critical}20`,
            borderRadius: tokens.borderRadius.sm,
          }}
        >
          {error}
        </div>
      )}

      {/* Actions */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: tokens.spacing.sm,
          flexDirection: isMobile ? "column" : "row",
          marginTop: tokens.spacing.md,
        }}
      >
        <button
          type="submit"
          disabled={connecting || !config.host}
          style={{
            flex: 1,
            padding: compact
              ? `8px ${tokens.spacing.md}`
              : `${tokens.spacing.sm} ${tokens.spacing.lg}`,
            minHeight: 44,
            fontSize: tokens.typography.fontSize.sm,
            fontWeight: tokens.typography.fontWeight.semibold,
            fontFamily: tokens.typography.fontFamily.primary,
            color: tokens.colors.text.inverse || "#fff",
            backgroundColor: connected
              ? tokens.colors.status.critical
              : tokens.colors.accent.primary,
            border: "none",
            borderRadius: tokens.borderRadius.md,
            cursor: connecting || !config.host ? "not-allowed" : "pointer",
            opacity: connecting || !config.host ? 0.6 : 1,
            transition: tokens.animation.fast,
          }}
        >
          {connecting ? "Connecting..." : connected ? "Disconnect" : "Connect"}
        </button>

        {parseUrlParams && !connected && (
          <button
            type="button"
            title="Copy share URL"
            onClick={() => {
              navigator.clipboard?.writeText(getShareUrl());
            }}
            style={{
              padding: compact ? "8px" : tokens.spacing.sm,
              minHeight: 44,
              width: isMobile ? "100%" : "auto",
              fontSize: tokens.typography.fontSize.xs,
              color: tokens.colors.text.tertiary,
              backgroundColor: "transparent",
              border: `1px solid ${tokens.colors.border.muted}`,
              borderRadius: tokens.borderRadius.md,
              cursor: "pointer",
              fontFamily: tokens.typography.fontFamily.primary,
            }}
          >
            Share
          </button>
        )}
      </div>
    </form>
  );
});

export default ConnectionForm;
